import * as React from 'react';
import { useCallback, useState, useMemo } from 'react';
import { useFormikContext } from 'formik';
import type { FormikProps } from 'formik';
import * as Immutable from 'immutable';
import { styled } from 'styled-components';

import { Button } from 'components/bootstrap';
import type Search from 'views/logic/search/Search';
import SearchBar from 'data-lake/preview/storage-types/internal/SearchBar';
import type { ActivePreviewSearch, DataLakeURLQueryParams } from 'data-lake/preview/Types';
import DataLakePreview from 'data-lake/preview/DataLakePreview';
import useInitialSearchBarFormValues from 'data-lake/preview/storage-types/external/hooks/useInitialFormValues';
import { WIDGET_DEFAULTS, MAX_LOADED_MESSAGES, EXTERNAL_DATA_LAKE_SEARCH_TYPE } from 'data-lake/preview/Constants';
import { onInitializingTimerange } from 'views/components/TimerangeForForm';
import useUserDateTime from 'hooks/useUserDateTime';
import { normalizeFromSearchBarForBackend } from 'views/logic/queries/NormalizeTimeRange';
import Query from 'views/logic/queries/Query';
import generateId from 'logic/generateId';
import type DataLakeWidgetConfig from 'data-lake/logic/DataLakeWidgetConfig';
import type DataLakeWidget from 'data-lake/logic/DataLakeWidget';
import type {
  ExternalDataLakeFormValues,
  ExternalDataLakeSearchType,
} from 'data-lake/preview/storage-types/external/Types';
import { EXTERNAL_DATA_LAKE_QUERY } from 'data-lake/preview/storage-types/external/Constants';
import TableFilter from 'data-lake/preview/storage-types/external/TableFilter';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import WidgetConfigurationProvider from 'data-lake/preview/storage-types/external/WidgetConfigurationProvider';
import DataLakeConnectorCreateInputModal, { ExternalDataLakeInputType } from 'data-lake/data-lake-connectors/DataLakeConnectorCreateInputModal';
import RetrievalProvider from 'data-lake/preview/storage-types/external/contexts/RetrievalProvider';
import TableList from 'data-lake/preview/storage-types/external/TableList';
import { Icon, IfPermitted } from 'components/common';
import useDataLakeTableOptions from 'data-lake/preview/storage-types/external/hooks/useDataLakeTableOptions';
import useFieldTypes from 'data-lake/hooks/useFieldTypes';
import type { FieldFilters, FieldsOperator } from 'data-lake/Types';
import Store from 'logic/local-storage/Store';

import PreviewLayoutProvider from './contexts/PreviewLayoutProvider';
const HIDE_DATA_LAKE_PREVIEW_TABLE_LIST = 'hide_data_lake_preview_table_list';

const RightColContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
  width: 100%;
`;

const StyledButton = styled(Button)`
  margin-right: 5px;
`;

const validate = (formValues: ExternalDataLakeFormValues) => {
  const errors = {};

  if (!formValues.table) {
    return { ...errors, table: 'Table is required' };
  }

  return errors;
};

const fieldFiltersArrayToString = (fieldFiltersArray: FieldFilters[]) => {
  if (!Array.isArray(fieldFiltersArray)) return '';

  return fieldFiltersArray.map((item) => `${item.field_name}=${item.value}`).join(',');
};

type ExternalDataLakeInputFields = {
  table?: string;
  fields?: {
    fieldFilters?: Array<FieldFilters>;
    operator?: FieldsOperator;
  };
};

type ExternalDataLakeSearchBarProps = {
  executedSearch: Search | undefined;
  onReset: () => void;
  onSubmit: (formValues: ExternalDataLakeFormValues) => void;
  formRef?: React.RefObject<FormikProps<ExternalDataLakeFormValues>>;
  searchJobIsRunning: boolean;
  initialUrlParams: DataLakeURLQueryParams;
  toggleTableList: () => void;
  showTableList: boolean;
};

const CreateInputActionButton = ({ dataLakeId }: { dataLakeId: string }) => {
  const { values } = useFormikContext<ExternalDataLakeInputFields>();
  const [showCreateInputModal, setShowCreateInputModal] = useState<boolean>(false);
  const toggleShowCreateInputModal = () => setShowCreateInputModal((cur) => !cur);

  return (
    <>
      <StyledButton onClick={toggleShowCreateInputModal} title="Create Input from my Search" bsStyle="primary">
        Create Input
      </StyledButton>
      {showCreateInputModal && (
        <DataLakeConnectorCreateInputModal
          connectorId={dataLakeId}
          tableId={values?.table}
          filterQuery={fieldFiltersArrayToString(values?.fields?.fieldFilters)}
          filterQueryOperator={values?.fields?.operator}
          onClose={toggleShowCreateInputModal}
        />
      )}
    </>
  );
};

const ExternalDataLakeSearchBar = ({
  executedSearch,
  onReset,
  onSubmit,
  formRef = undefined,
  searchJobIsRunning,
  initialUrlParams,
  toggleTableList,
  showTableList,
}: ExternalDataLakeSearchBarProps) => {
  const initialFormValues = useInitialSearchBarFormValues(executedSearch, initialUrlParams);
  const { setDataLakeFieldsFetchQuery, selectedDataLake } = useSelectedDataLake();
  const { isLoading: isLoadingFields } = useFieldTypes();
  const _onReset = useCallback(() => {
    setDataLakeFieldsFetchQuery(null);

    return onReset();
  }, [onReset, setDataLakeFieldsFetchQuery]);

  const { data: tables, isLoading: isLoadingTables } = useDataLakeTableOptions(selectedDataLake?.id);
  const hasOnlyOneCatalog = useMemo(
    () =>
      tables?.length > 0 &&
      tables.every((item) => item.catalog === tables[0].catalog && item.schema === tables[0].schema),
    [tables],
  );

  return (
    <SearchBar<ExternalDataLakeFormValues>
      rightColumn={
        <RightColContainer>
          <TableFilter tables={tables} isLoading={isLoadingTables} hasOnlyOneCatalog={hasOnlyOneCatalog} />
          <Button onClick={toggleTableList}>
            <Icon name="list" />
            {showTableList ? 'Hide' : 'Show'} Table List
          </Button>
        </RightColContainer>
      }
      actions={
        <IfPermitted permissions={[`inputs:create:${ExternalDataLakeInputType}`]}>
          <CreateInputActionButton dataLakeId={selectedDataLake?.id} />
        </IfPermitted>
      }
      validate={validate}
      emptyFormValues={{
        timerange: WIDGET_DEFAULTS.timerange,
        table: undefined,
        fields: undefined,
      }}
      disableSubmit={isLoadingTables || isLoadingFields}
      initialValues={initialFormValues}
      onReset={_onReset}
      onSubmit={onSubmit}
      formRef={formRef}
      searchJobIsRunning={searchJobIsRunning}
    />
  );
};

type Props = {
  initialSearch: Search | undefined;
  initialSearchJob: { status: ActivePreviewSearch['status']; id: string; nodeId: string; progress: number } | undefined;
  initialUrlParams: DataLakeURLQueryParams;
  resetUrlQueryParams: () => void;
};

const ExternalDataLakePreview = ({ initialSearch, initialSearchJob, initialUrlParams, resetUrlQueryParams }: Props) => {
  const { formatTime, userTimezone } = useUserDateTime();
  const { selectedDataLake } = useSelectedDataLake();
  const [showTableList, setShowTableList] = useState<boolean>(Store.get(HIDE_DATA_LAKE_PREVIEW_TABLE_LIST) !== 'true');

  const toggleTableList = useCallback(() => {
    setShowTableList((cur) => {
      Store.set(HIDE_DATA_LAKE_PREVIEW_TABLE_LIST, cur ? 'true' : 'false');

      return !cur;
    });
  }, []);

  const renderSearchBar = useCallback(
    ({
      executedSearch,
      onReset,
      onSubmit,
      searchJobIsRunning,
      formRef,
    }: Omit<ExternalDataLakeSearchBarProps, 'initialUrlParams'>) => (
      <ExternalDataLakeSearchBar
        showTableList={showTableList}
        formRef={formRef}
        executedSearch={executedSearch}
        toggleTableList={toggleTableList}
        onReset={onReset}
        onSubmit={onSubmit}
        searchJobIsRunning={searchJobIsRunning}
        initialUrlParams={initialUrlParams}
      />
    ),
    [initialUrlParams, showTableList, toggleTableList],
  );

  const widgetFromFormValues = useCallback(
    (widget: DataLakeWidget, { fields, timerange, table }: ExternalDataLakeFormValues) => {
      const updatedWidgetConfig: DataLakeWidgetConfig = widget.config
        .toBuilder()
        .fieldFilters(fields?.fieldFilters ? Immutable.List(fields.fieldFilters) : widget.config.fieldFilters)
        .filtersOperator(fields?.operator ?? widget.config.filtersOperator)
        .build();

      return widget
        .toBuilder()
        .config(updatedWidgetConfig)
        .timerange(timerange ? normalizeFromSearchBarForBackend(timerange, userTimezone) : widget.timerange)
        .streams([table])
        .build();
    },
    [userTimezone],
  );

  const filtersFromSearchType = useCallback(
    (searchType: ExternalDataLakeSearchType) => ({
      table: searchType.table_id,
      timerange: onInitializingTimerange(searchType.timerange, formatTime),
      fields: {
        fieldFilters: searchType.field_filters ?? WIDGET_DEFAULTS.fieldFilters,
        operator: searchType.filters_operator ?? WIDGET_DEFAULTS.fieldsOperator,
      },
    }),
    [formatTime],
  );
  const queryFromFormValues = useCallback(
    (widget: DataLakeWidget, { table }: ExternalDataLakeFormValues) =>
      Query.builder()
        .id(generateId())
        .query(EXTERNAL_DATA_LAKE_QUERY)
        .searchTypes([
          {
            size: MAX_LOADED_MESSAGES,
            filters_operator: widget.config.filtersOperator,
            timerange: widget.timerange,
            table_id: table,
            data_lake_id: selectedDataLake?.id,
            field_filters: widget.config.fieldFilters.toArray(),
            fields: widget.config.fields.toArray(),
            type: EXTERNAL_DATA_LAKE_SEARCH_TYPE,
            filters: [],
          } as unknown as ExternalDataLakeSearchType,
        ])
        .build(),
    [selectedDataLake?.id],
  );

  const renderTableList = useCallback(
    (searchBarFormRef: React.RefObject<FormikProps<ExternalDataLakeFormValues>>) => (
      <TableList searchBarFormRef={searchBarFormRef} onClose={toggleTableList} />
    ),
    [toggleTableList],
  );

  return (
    <WidgetConfigurationProvider initialSearch={initialSearch}>
      <RetrievalProvider>
        <PreviewLayoutProvider>
          <DataLakePreview<ExternalDataLakeFormValues>
            filtersFromSearchType={filtersFromSearchType}
            initialSearch={initialSearch}
            initialSearchJob={initialSearchJob}
            queryFromFormValues={queryFromFormValues}
            resetUrlQueryParams={resetUrlQueryParams}
            searchBar={renderSearchBar}
            leftCol={showTableList ? renderTableList : undefined}
            widgetFromFormValues={widgetFromFormValues}
          />
        </PreviewLayoutProvider>
      </RetrievalProvider>
    </WidgetConfigurationProvider>
  );
};

export default ExternalDataLakePreview;
